import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import useLanguage from "../../utils/useLanguage";
import nextIcon from "../../assets/next_icon.png";
import previousIcon from "../../assets/previous_icon.png";
import editIcon from "../../assets/edit_icon.png";
import closeIcon from "../../assets/close_icon.png";
import saveIcon from "../../assets/save_icon.png";
import addIcon from "../../assets/add_icon.png";
import { UserAuth } from "../../utils/AuthContext";
import Loader from "../../components/Loader";
import { Link } from "react-router-dom";
import ErrorBox from "../../components/ErrorBox";
import { DataContext } from "../../utils/DataContext";
import BackToDashboard from "../../components/BackToDashboard";
import ErrorMessage from "../../components/ErrorMessage";
import RoundedTable, {
  TABLE_CELL_TYPES,
} from "../../components/table-component/RoundedTable";
import PageHeadline from "../../components/PageHeadline";
import BanksTransactionModal from "../../components/BanksTransactionModal";
import SectionDivider from "../../components/SectionDivider";
import deleteIcon from "../../assets/delete_Icon.png";
import transactionsIcon from "../../assets/transactions_icon.png";
import InformationBox from "../../components/InformationBox";
import PageWrapper from "../../components/PageWrapper";

export default function BanksWithTransaction() {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showRemoveBankModal, setShowRemoveBankModal] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);
  const [bankModal, setBankModal] = useState(false);
  const [bankFields, setBankFields] = useState({});

  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [error, setError] = useState(false);
  const [shouldGetCustomers, setShouldGetCustomers] = useState(false);

  const [errorMessage2, setErrorMessage2] = useState("");

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: banks?.length,
  });

  const [loadingModal, setLoadingModal] = useState(false);
  const [show2btns, setShow2btns] = useState(false);
  const [transactionsModal, setTransactionsModal] = useState(false);

  const { userData } = useContext(UserAuth);
  const { loading } = useContext(DataContext);

  const handleToggleTransactionsModal = (customer) => {
    setTransactionsModal((prev) => !prev);
    setSelectedCustomer(customer);
  };

  const openBankModal = (bank) => {
    setSelectedBank(bank);
    setBankFields({
      bankName: bank.name,
      bankNameWithNoSpace: bank.nameWithNoSpace,
      branches: bank.branch || [],
    });
    setIsEditing(false);
    setBankModal(true);
  };

  const handleBankInputChange = (e, fieldName) => {
    const { value } = e.target;
    setBankFields((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleBranchChange = (index, field, value) => {
    const updatedBranches = [...bankFields.branches];
    updatedBranches[index] = {
      ...updatedBranches[index],
      [field]: value,
    };
    setBankFields({
      ...bankFields,
      branches: updatedBranches,
    });
  };

  const addBranch = () => {
    setBankFields({
      ...bankFields,
      branches: [
        ...bankFields.branches,
        { name: "", address: "", bankId: selectedBank?.id },
      ],
    });
  };

  const removeBranch = (index) => {
    const updatedBranches = bankFields.branches.filter((_, i) => i !== index);
    setBankFields({
      ...bankFields,
      branches: updatedBranches,
    });
  };

  const handleSaveBank = async () => {
    try {
      setLoadingModal(true);
      const response = await axios.put(
        `${process.env.REACT_APP_URL}/api/bank/${selectedBank?.id}`,
        {
          name: bankFields.bankName,
          nameWithNoSpace: bankFields.bankNameWithNoSpace,
          branches: bankFields.branches,
        },
        {
          headers: {
            Authorization: `Bearer ${userData?.token}`,
          },
        }
      );

      if (response.status === 200) {
        setBankModal(false);
        // Refresh the banks list
        getAllBanks(pagination.currentPage, 5, undefined, searchTerm);
      }
      setLoadingModal(false);
    } catch (error) {
      console.error("Error updating bank:", error);
      setLoadingModal(false);
    }
  };

  const openRemoveBankModal = (bank) => {
    setSelectedBank(bank);
    setShowRemoveBankModal(true);
  };

  const handleDeleteBank = async () => {
    setLoadingModal(true);
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_URL}/api/bank/${selectedBank.id}`,
        {
          headers: {
            Authorization: `Bearer ${userData?.token}`,
          },
        }
      );

      if (response.status === 200) {
        setShowRemoveBankModal(false);
        setBankModal(false);
        // Refresh the banks list
        getAllBanks(pagination.currentPage, 5, undefined, searchTerm);
      }
    } catch (error) {
      console.error("Error deleting bank:", error);
    } finally {
      setLoadingModal(false);
      setBankModal(false);
    }
  };

  // Updated handleCancel function
  const handleCancelBankEdit = () => {
    setBankModal(false);
    setSelectedBank(null);
    setShowRemoveBankModal(false);
  };

  const titles = [
    { value: "bank" },
    { value: "balance" },
    { value: "transactions", keepOnMobile: true },
  ];

  const rows = banks?.map((item) => [
    {
      value: <Link onClick={() => openBankModal(item)}>{item?.name}</Link>,
      type: TABLE_CELL_TYPES.ELEMENT,
      keepOnMobile: true,
    },
    {
      value: item.balance,
      currency: t(item?.currency || "USD"),
      type: TABLE_CELL_TYPES.MONEY,
    },
    {
      value: (
        <CustomButton
          variant="blank"
          icon={transactionsIcon}
          iconSize={25}
          title={t("transactions")}
          onClick={() => handleToggleTransactionsModal(item)}
        />
      ),
      type: TABLE_CELL_TYPES.ELEMENT,
      keepOnMobile: true,
    },
  ]);

  const getAllBanks = async (page, limit, searchQuery) => {
    try {
      setLoadingBanks(true);
      const banks = await axios.get(`${process.env.REACT_APP_URL}/api/bank`, {
        headers: {
          Authorization: `Bearer ${userData?.token}`,
          "organization-id": userData?.organizationId,
        },
        params: {
          page: page,
          limit: limit,
          searchQuery: searchQuery,
          isIraqi: false,
        },
      });

      if (banks.status === 200) {
        setBanks(banks?.data?.banks);
        setPagination({
          totalPages: banks.data.totalPages,
          totalRecords: banks.data.totalRecords,
          currentPage: banks.data.page,
        });
        setLoadingBanks(false);
      }
      return banks;
    } catch (error) {
      setError(error?.response?.message || "Failed");
      console.error("Failed to create the supplier", error);
      setLoadingBanks(false);
    }
  };

  useEffect(() => {
    getAllBanks(pagination.currentPage, 5, undefined, searchTerm);
  }, [pagination.currentPage, searchTerm]);

  return (
    <div className="e-container">
      <PageWrapper
          style={{ direction: currentDirection }}
          title={t("organizations")}
          withBackButton
      >
        <div>
          <BackToDashboard
            title={t("back")}
            variant="blank-fit-content"
            icon={currentDirection === "ltr" ? previousIcon : nextIcon}
            iconSize={14}
            to={
              userData?.userType === "user"
                ? "/invoiceManagement"
                : `/${userData?.organizationType?.toLowerCase()}-dashboard`
            }
          />
        </div>
        {errorMessage2 && <ErrorBox>{errorMessage2}</ErrorBox>}
        <div className="o-row o-row--horizontal-align_space-between u-margin--top_700 u-margin--bottom_700">
          <PageHeadline title={"banks"} />
        </div>

        {loadingBanks ? (
          <Loader />
        ) : banks.length === 0 ? (
          <InformationBox>{t("noBanksFound")}</InformationBox>
        ) : (
          <>
            <CustomInput
              name="name"
              value={searchTerm}
              placeholder={searchTerm ? "" : t("search")}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <ul className="users-list">
              {errorMessage2 && <ErrorMessage>{errorMessage2}</ErrorMessage>}

              <RoundedTable
                withCheckbox={show2btns}
                titles={titles}
                rows={rows}
                fullData={banks}
                pagination={pagination}
                setPagination={setPagination}
                selectedRows={selectedCustomers}
                setSelectedRows={setSelectedCustomers}
                onSelectAll={getAllBanks}
              />
            </ul>
          </>
        )}
        {bankModal && (
          <Modal
            title={isEditing ? t("editBank") : t("bank")}
            onClose={handleCancelBankEdit}
            footer={
              <>
                {loadingModal ? <Loader /> : null}
                <section className="edit-user-actions">
                  {isEditing ? (
                    <>
                      <CustomButton
                        title={t("confirm")}
                        onClick={handleSaveBank}
                        icon={saveIcon}
                        iconSize={16}
                        disabled={loadingModal}
                      />
                      <CustomButton
                        title={t("cancel")}
                        onClick={handleCancelBankEdit}
                        variant="blank"
                        icon={closeIcon}
                        iconSize={14}
                      />
                    </>
                  ) : (
                    <>
                      <CustomButton
                        variant="blank"
                        title={t("edit")}
                        onClick={() => setIsEditing(true)}
                        icon={editIcon}
                        iconSize={18}
                      />
                      <CustomButton
                        title={t("delete")}
                        variant="red"
                        icon={deleteIcon}
                        iconSize={18}
                        onClick={() => openRemoveBankModal(selectedBank)}
                      />
                    </>
                  )}
                </section>
              </>
            }
          >
            <div>
              <section>
                <div>
                  {isEditing ? (
                    <div className="edit-user-entity">
                      <span>
                        {t("bankName", { defaultValue: "Bank Name" })}:{" "}
                      </span>
                      <CustomInput
                        name="bankName"
                        value={bankFields.bankName || ""}
                        onChange={(e) => handleBankInputChange(e, "bankName")}
                      />
                    </div>
                  ) : (
                    <div className="view-user-entity">
                      <p>{t("bankName", { defaultValue: "Bank Name" })}:</p>
                      <p>{selectedBank?.name}</p>
                    </div>
                  )}
                </div>
              </section>
              <section>
                <div>
                  {isEditing ? (
                    <div>
                      <div className="edit-user-entity">
                        <SectionDivider title={"branches"} />
                        <div>
                          {bankFields.branches?.map((branch, index) => (
                            <div
                              key={index}
                              className="u-margin--bottom_400 u-padding_400 u-border_1_gray"
                            >
                              <div className="edit-user-entity">
                                <span>
                                  {t("branchName", {
                                    defaultValue: "Branch Name",
                                  })}
                                  :{" "}
                                </span>
                                <CustomInput
                                  value={branch.name || ""}
                                  onChange={(e) =>
                                    handleBranchChange(
                                      index,
                                      "name",
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                              <div className="edit-user-entity">
                                <span>
                                  {t("address", { defaultValue: "Address" })}:{" "}
                                </span>
                                <CustomInput
                                  value={branch.address || ""}
                                  onChange={(e) =>
                                    handleBranchChange(
                                      index,
                                      "address",
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                              <div className="o-row o-row--horizontal-align_end">
                                <CustomButton
                                  title={t("removeBranch")}
                                  variant="red"
                                  icon={deleteIcon}
                                  onClick={() => removeBranch(index)}
                                  iconSize={14}
                                />
                              </div>
                            </div>
                          ))}
                          <div className="o-row o-row--horizontal-align_end">
                            <CustomButton
                              title={t("addBranch", {
                                defaultValue: "Add Branch",
                              })}
                              variant="blank"
                              onClick={addBranch}
                              icon={addIcon}
                              iconSize={16}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="">
                      <SectionDivider title={"branches"} />
                      <div className="o-column o-column--gap-size_400">
                        {selectedBank?.branch?.map((branch, index) => (
                          <div key={index}>
                            <div className="view-user-entity">
                              <p>{t("branchName")}: </p>
                              <p>{branch.name}</p>
                            </div>
                            <div className="view-user-entity">
                              <p>{t("branchAddress")}: </p>
                              <p>{branch.address}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </section>
            </div>
          </Modal>
        )}
        {showRemoveBankModal && (
          <Modal
            title={t("deleteBank")}
            onClose={() => setShowRemoveBankModal(false)}
            footer={
              <>
                {loadingModal ? <Loader /> : null}
                <section className="delete-user-actions">
                  <CustomButton
                    onClick={handleDeleteBank}
                    title={t("confirm")}
                    icon={saveIcon}
                    iconSize={16}
                    disabled={loadingModal}
                  />
                  <CustomButton
                    title={t("cancel")}
                    onClick={() => setShowRemoveBankModal(false)}
                    variant="blank"
                    icon={closeIcon}
                    iconSize={14}
                  />
                </section>
              </>
            }
          >
            <div className="delete-modal-body">
              <h3>{t("areYouSureYouWantToDeleteThisBank")}</h3>
              <p>{selectedBank?.name}</p>
            </div>
          </Modal>
        )}

        {transactionsModal ? (
          <BanksTransactionModal
            selected={selectedCustomer}
            handleToggleTransactionsModal={handleToggleTransactionsModal}
            setShouldGetCustomers={setShouldGetCustomers}
          />
        ) : null}
      </PageWrapper>
    </div>
  );
}
